import React, { useRef, useCallback, useEffect } from 'react'
import { useLocation } from 'react-router-dom'

import { EMBEDDED_SIGNIN_POPUP_WINDOW_NAME } from '@sketch/constants'
import { SKETCH_WEBSITE } from '@sketch/env-config'

import { Button, Link } from '@sketch/components'

import { createLocalStorageSubscription } from '@sketch/utils'
import {
  useUserSignedIn,
  routes,
  useSignOut,
  IndexLayoutOld,
} from '@sketch/modules-common'

import { ReactComponent as LockIcon } from '@sketch/icons/lock-64'

import {
  BottomText,
  ButtonWrapper,
  StyledErrorMessage,
} from '../RequiresMembershipView/RequiresMembershipView.styles'

type StorageEventListener =
  | ((event: StorageEvent) => void | undefined)
  | undefined

const useSignInPopup = (onSignIn: () => void) => {
  const popupRef = useRef<Window | null>(null)
  const storageListener = useRef<StorageEventListener | undefined>(undefined)

  // cleanup popup window and listener references
  const reset = useCallback(() => {
    window.removeEventListener('storage', storageListener.current!)
    storageListener.current = undefined
    popupRef.current?.close()
    popupRef.current = null
  }, [])

  // open signin window
  const open = useCallback(() => {
    // cleanup any previous instance
    reset()
    // open window
    popupRef.current = window.open(
      routes.SIGN_IN.create({}),
      EMBEDDED_SIGNIN_POPUP_WINDOW_NAME,
      'toolbar=0,location=0,menubar=0,width=640,height=700'
    )
    // wait for storage change following successful sign-in
    const subscription = createLocalStorageSubscription()
    storageListener.current = subscription?.trackStorageChanges(
      'userProfile',
      () => {
        reset()
        onSignIn()
      }
    )
  }, [reset, onSignIn])

  // cleanup on unmount
  useEffect(() => reset, [reset])

  return { open, close: reset }
}

const RequiresSignInEmbeddedView = () => {
  const location = useLocation()
  const isUserSignedIn = useUserSignedIn()
  const signOut = useSignOut({
    redirectLocation: location,
    reason: 'Switching account in Embedded View',
  })
  const onSignIn = () => window.location.reload()
  const { open: openSignInWindow } = useSignInPopup(onSignIn)

  // sign out and reload
  const switchAccount = () => {
    signOut()
    window.location.reload()
  }

  return (
    <IndexLayoutOld headerLink="none" showFooter={false}>
      <StyledErrorMessage
        icon={<LockIcon />}
        iconSize="medium"
        title="This document is private"
        extra={
          <ButtonWrapper>
            {isUserSignedIn ? (
              <Button onClick={switchAccount} variant="primary">
                Switch Account
              </Button>
            ) : (
              <Button onClick={openSignInWindow} variant="primary">
                Sign In
              </Button>
            )}
          </ButtonWrapper>
        }
        description="You don’t have permission from the owner to view this document."
      />
      <BottomText textStyle="copy.tertiary.standard.D">
        Need Help?{' '}
        <Link
          href={`${SKETCH_WEBSITE}/support/`}
          isUnderlined
          external
          target="_blank"
        >
          Get in touch
        </Link>
      </BottomText>
    </IndexLayoutOld>
  )
}

export default RequiresSignInEmbeddedView
